import { cn } from '@/lib/utils'
import { SquarePlay } from 'lucide-react'
import { AdPreview } from '@/components/ui/ad-preview'
import { useContext, useEffect, useState } from 'react'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import iconPlaceholder from '@/assets/images/media-buying/icon-placeholder.png'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_PAGE_DESCRIPTION_DATA } from '@/graphql/media-buying-queries'
import { CLEAN_UP_AD_CREATIVE_ASSETS } from '@/graphql/ad-creatives-library-queries'
import { showNotification } from '@/utils/notifications'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { SettingsContext } from '@/context/settings-context'
import WizardSteps from '@/components/common/wizard/WizardSteps'

const CarouselIcon = () => {
    return (
        <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6 0.75H14C14.6904 0.75 15.25 1.30964 15.25 2V14C15.25 14.6904 14.6904 15.25 14 15.25H6C5.30964 15.25 4.75 14.6904 4.75 14V2C4.75 1.30964 5.30964 0.75 6 0.75Z"
                stroke="#899CBD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M4.5 13H2C1.44772 13 1 12.5523 1 12V4C1 3.44772 1.44772 3 2 3H4.5"
                stroke="#899CBD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15.5 13H18C18.5523 13 19 12.5523 19 12V4C19 3.44772 18.5523 3 18 3H15.5"
                stroke="#899CBD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export const SelectFormat = () => {
    const {
        id: adCreativeId,
        editCreativeId,
        cloneCreativeId,
        getStepContentData,
        setStepContentData,
        registerNextGuard,
        setVisible,
        setCampaignFormat,
        clearWizardStorage,
        dataForStepper,
    } = useWizard()
    const isEditMode = !!editCreativeId || !!cloneCreativeId
    const { connectedPublishers } = useMediaBuyingContext()
    const [format, setFormat] = useState('singleImage')
    const [showLeadForm, setShowLeadForm] = useState(false)
    const [pageTitle, setPageTitle] = useState('Sunny Travels')
    const [pageIcon, setPageIcon] = useState(iconPlaceholder)
    const { theme } = useContext(SettingsContext)
    const [cleanUpAdCreativeAssets] = useMutation(CLEAN_UP_AD_CREATIVE_ASSETS)

    const [fetchPageDescriptionData, { data: pageDescriptionData }] = useLazyQuery(GET_PAGE_DESCRIPTION_DATA, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error('User Page Data failed:', err)
            err.graphQLErrors.forEach(({ message, extensions }) => {
                if (extensions.code === 'PUBLISHER_API_ERROR') {
                    console.error('Caught publisher API error:', message)
                    showNotification(message, 'error')
                }
            })
        },
    })

    useEffect(() => {
        if (pageDescriptionData) {
            const { name, picture } = pageDescriptionData.publisherPageData || {}
            setPageIcon(picture?.data?.url || iconPlaceholder)
            setPageTitle(name || 'Sunny Travels')
        }
    }, [pageDescriptionData])

    useEffect(() => {
        setStepContentData({
            format,
            showLeadForm,
        })
    }, [format, showLeadForm])

    useEffect(() => {
        // set next button guard
        registerNextGuard(handleNext)

        // set page data storage
        const cachedData = getStepContentData()
        if (cachedData) {
            setFormat(cachedData.format || format)
            setShowLeadForm(cachedData.showLeadForm || showLeadForm)
        } else {
            setStepContentData({
                format,
                showLeadForm,
            })
        }
    }, [])

    useEffect(() => {
        if (connectedPublishers) {
            // fetch page description data
            const thisPublisher = Object.values(connectedPublishers)
            const getPageData = async () => {
                await fetchPageDescriptionData({
                    variables: {
                        publisherId: thisPublisher?.[0].publisher_id,
                        pageId: thisPublisher?.[0].page_id,
                        campaignId: thisPublisher?.[0].campaign_id,
                    },
                })
            }

            getPageData()
        }
    }, [connectedPublishers])

    useEffect(() => {
        document.body.classList.remove('light', 'dark')
        document.body.classList.add(theme)
    }, [theme])

    const handleNext = () => {
        return true
    }

    const textColor = theme === 'dark' ? 'text-white' : 'text-dark'

    const handleFormatChange = (format) => {
        cleanUpAdCreativeAssets({ variables: { adCreativeId } })
        clearWizardStorage()
        setFormat(format)
        setCampaignFormat(format)
        setStepContentData({ format })

        setVisible('videoTrim', format === 'video')
        setVisible('carouselCards', format === 'carousel')
        setVisible('setMediaCrop', format !== 'carousel')
        setVisible('aiTextToImage', false)
        setVisible('aiAssetLibrary', false)
    }

    return (
        <div className="tw:h-full tw:flex">
            <WizardSteps adCreative={dataForStepper} currentStep="selectFormat" />
            <div className="flex-grow-1 tw:pt-4 tw:pl-4 tw:pr-4 overflow-auto">
                <div className="tw:flex tw:h-full">
                    <div className="flex-grow-1 pe-4 overflow-auto">
                        <div className="tw:flex tw:flex-col tw:!gap-4">
                            <div
                                className={cn('tw:flex tw:items-start tw:!gap-2', {
                                    'tw:opacity-50 tw:cursor-not-allowed': isEditMode,
                                })}
                                onClick={!isEditMode ? () => handleFormatChange('singleImage') : undefined}
                            >
                                <input
                                    type="radio"
                                    id="singleImage"
                                    name="adFormat"
                                    className="form-check-input custom-radio tw:min-w-4 tw:min-h-4"
                                    checked={format === 'singleImage'}
                                    onChange={() => !isEditMode && handleFormatChange('singleImage')}
                                    disabled={isEditMode}
                                />
                                <div
                                    className={cn('w-100 ms-2', { 'tw:cursor-pointer': !isEditMode, 'tw:cursor-not-allowed': isEditMode })}
                                    onClick={!isEditMode ? () => handleFormatChange('singleImage') : undefined}
                                >
                                    <div className="tw:flex tw:items-center tw:gap-2">
                                        <i className="bi bi-image tw-w-[18px] tw-h-[18px] tw:text-gray-200" />
                                        <label
                                            htmlFor="singleImage"
                                            className={cn('tw:font-semibold tw:text-sm', { 'tw:cursor-pointer': !isEditMode, 'tw:cursor-not-allowed': isEditMode })}
                                        >
                                            Single image
                                        </label>
                                    </div>
                                    <p
                                        className={cn(
                                            'tw:!mt-1 tw:!mb-0 tw:!leading-[1.5] tw:text-sm tw:ml-6',
                                            textColor,
                                        )}
                                    >
                                        Drive people to destination websites or apps through high‐quality and engaging
                                        visuals. Use your own images or create an ad with stock photos to tell your
                                        story.
                                    </p>
                                </div>
                            </div>

                            {/* Video */}
                            <div
                                className={cn('tw:flex tw:items-start tw:!gap-2', {
                                    'tw:opacity-50 tw:cursor-not-allowed': isEditMode,
                                })}
                                onClick={!isEditMode ? () => handleFormatChange('video') : undefined}
                            >
                                <input
                                    type="radio"
                                    id="video"
                                    name="adFormat"
                                    className="form-check-input custom-radio tw:!mt-0 tw:min-w-4 tw:min-h-4"
                                    checked={format === 'video'}
                                    onChange={() => !isEditMode && handleFormatChange('video')}
                                    disabled={isEditMode}
                                />
                                <div className={cn('w-100 ms-2', { 'tw:cursor-pointer': !isEditMode, 'tw:cursor-not-allowed': isEditMode })}>
                                    <div className="tw:flex tw:items-center tw:gap-2">
                                        <SquarePlay size={18} className="tw-w-[18px] tw-h-[18px] tw:text-gray-200" />
                                        <label
                                            htmlFor="video"
                                            className={cn('tw:font-semibold tw:text-sm', { 'tw:cursor-pointer': !isEditMode, 'tw:cursor-not-allowed': isEditMode })}
                                        >
                                            Single Video
                                        </label>
                                    </div>
                                    <p
                                        className={cn(
                                            'tw:!mt-1 tw:!mb-0 tw:!leading-[1.5] tw:text-sm tw:ml-6',
                                            textColor,
                                        )}
                                    >
                                        Ads for property listings, home insurance, mortgages, or other related
                                        opportunities.
                                    </p>
                                </div>
                            </div>

                            {/* Carousel */}
                            <div
                                className={cn('tw:flex tw:items-start tw:!gap-2', {
                                    'tw:opacity-50 tw:cursor-not-allowed': isEditMode,
                                })}
                                onClick={!isEditMode ? () => handleFormatChange('carousel') : undefined}
                            >
                                <input
                                    type="radio"
                                    id="carousel"
                                    name="adFormat"
                                    className="form-check-input custom-radio tw:!mt-0 tw:min-w-4 tw:min-h-4"
                                    checked={format === 'carousel'}
                                    onChange={() => !isEditMode && handleFormatChange('carousel')}
                                    disabled={isEditMode}
                                />
                                <div className={cn('w-100 ms-2', { 'tw:cursor-pointer': !isEditMode, 'tw:cursor-not-allowed': isEditMode })}>
                                    <div className="tw:flex tw:items-center tw:gap-2">
                                        <CarouselIcon className="tw-w-[18px] tw-h-[18px] tw:text-gray-200" />
                                        <label
                                            htmlFor="singleCarousel"
                                            className={cn('tw:font-semibold tw:text-sm', { 'tw:cursor-pointer': !isEditMode, 'tw:cursor-not-allowed': isEditMode })}
                                        >
                                            Carousel
                                        </label>
                                    </div>
                                    <p
                                        className={cn(
                                            'tw:!mt-1 tw:!mb-0 tw:!leading-[1.5] tw:text-sm tw:ml-6',
                                            textColor,
                                        )}
                                    >
                                        Display up to ten images or videos within a single ad, each with its own link.
                                        Use carousel ads to show multiple products or features in one placement.
                                    </p>
                                </div>
                            </div>

                            {/* Lead Form Checkbox */}
                            <div className="tw:flex tw:flex-col tw:!gap-6">
                                <div className="tw:flex tw:items-center tw:!gap-2">
                                    <input
                                        type="checkbox"
                                        id="leadForm"
                                        className="tw:!mt-0"
                                        checked={showLeadForm}
                                        onChange={() => {
                                            clearWizardStorage()
                                            setShowLeadForm((prev) => {
                                                const newValue = !prev
                                                setVisible('leadForm', newValue)
                                                setStepContentData({ format, showLeadForm: newValue })
                                                setCampaignFormat(format)
                                                return newValue
                                            })
                                        }}
                                    />
                                    <label htmlFor="leadForm" className="tw:font-semibold tw:text-sm">
                                        Attach Instant Form
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* RECOMMENDATIONS SECTION */}
                        {format === 'singleImage' && (
                            <div className="tw:mt-6">
                                <h5 className="tw:mb-3 tw:!text-base tw:!font-semibold">Recommendations</h5>

                                <div className="tw:mt-4 tw:text-sm tw:font-semibold">Design Recommendations</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">File type: JPG or PNG</li>
                                    <li className="wizard-ad-format-label">Ratio: 1.91:1 to 4:5</li>
                                    <li className="wizard-ad-format-label">
                                        Resolution: 1:1 ratio (1440×1440 pixels) or 4:5 ratio (1440×1800 pixels)
                                    </li>
                                </ul>

                                <div className="mb-1 tw:text-sm tw:font-semibold">Text Recommendations</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">Primary text: 50–150 characters</li>
                                    <li className="wizard-ad-format-label">Headline: 27 characters</li>
                                </ul>

                                <div className="mb-1 tw:text-sm tw:font-semibold">Technical Requirements</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">Maximum file size: 30 MB</li>
                                    <li className="wizard-ad-format-label">Minimum width: 600 pixels</li>
                                    <li className="wizard-ad-format-label">Minimum height: 1:1 ratio: 600 pixels</li>
                                    <li className="wizard-ad-format-label">4:5 ratio: 750 pixels</li>
                                    <li className="wizard-ad-format-label">Aspect ratio tolerance: 3%</li>
                                </ul>
                            </div>
                        )}

                        {format === 'carousel' && (
                            <div className="tw:mt-6">
                                <h5 className="tw:mb-3 tw:!text-base tw:!font-semibold">Recommendations</h5>

                                <div className="tw:mt-4 tw:text-sm tw:font-semibold">Design Recommendations</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">File type: JPG or PNG</li>
                                    <li className="wizard-ad-format-label">Ratio: 1.91:1 to 4:5</li>
                                    <li className="wizard-ad-format-label">
                                        Resolution: 1:1 ratio (1440×1440 pixels) or 4:5 ratio (1440×1800 pixels)
                                    </li>
                                </ul>

                                <div className="mb-1 tw:text-sm tw:font-semibold">Text Recommendations</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">Primary text: 50–150 characters</li>
                                    <li className="wizard-ad-format-label">Headline: 27 characters</li>
                                </ul>

                                <div className="mb-1 tw:text-sm tw:font-semibold">Technical Requirements</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">Maximum file size: 30 MB</li>
                                    <li className="wizard-ad-format-label">Minimum width: 600 pixels</li>
                                    <li className="wizard-ad-format-label">Minimum height: 1:1 ratio: 600 pixels</li>
                                    <li className="wizard-ad-format-label">4:5 ratio: 750 pixels</li>
                                    <li className="wizard-ad-format-label">Aspect ratio tolerance: 3%</li>
                                </ul>
                            </div>
                        )}

                        {format === 'video' && (
                            <div className="tw:mt-6">
                                <h5 className="tw:mb-3 tw:!text-base tw:!font-semibold">Recommendations</h5>

                                <div className="tw:mt-4 tw:text-sm tw:font-semibold">Design Recommendations</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">File type: MP4, MOV or GIF</li>
                                    <li className="wizard-ad-format-label">
                                        Ratio: 1:1 (for desktop or mobile) or 4:5 (for mobile only)
                                    </li>
                                    <li className="wizard-ad-format-label">
                                        Video settings: H.264 compression, square pixels, fixed frame rate, progressive
                                        scan and stereo AAC audio compression at over 128 kbps
                                    </li>
                                    <li className="wizard-ad-format-label">
                                        Resolution:
                                        <div className="tw:ml-5">
                                            1:1 ratio: 1440 × 1440 pixels
                                            <br />
                                            4:5 ratio: 1440 × 1800 pixels
                                        </div>
                                    </li>
                                    <li className="wizard-ad-format-label">
                                        Video captions: Optional, but recommended
                                    </li>
                                    <li className="wizard-ad-format-label">Video sound: Optional, but recommended</li>
                                    <li className="wizard-ad-format-label">
                                        Videos should not contain edit lists or special boxes in file containers
                                    </li>
                                    <li className="wizard-ad-format-label">
                                        The thumbnail in the ad footer is sourced directly from the video thumbnail
                                        chosen by advertisers during the ad creation workflow.
                                    </li>
                                </ul>

                                <div className="tw:mt-4 tw:text-sm tw:font-semibold">Text Recommendations</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">Primary text: 50–150 characters</li>
                                    <li className="wizard-ad-format-label">Headline: 27 characters</li>
                                </ul>

                                <div className="tw:mt-4 tw:text-sm tw:font-semibold">Technical Requirements</div>
                                <ul className="mt-2 tw:list-disc tw:list-inside tw:!pl-0">
                                    <li className="wizard-ad-format-label">Video duration: 1 second to 241 minutes</li>
                                    <li className="wizard-ad-format-label">Maximum file size: 4 GB</li>
                                    <li className="wizard-ad-format-label">Minimum width: 120 pixels</li>
                                    <li className="wizard-ad-format-label">Minimum height: 120 pixels</li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* mock phone */}
                    <div className="border-left-white-1 ps-4">
                        <AdPreview
                            format={format}
                            pageTitle={pageTitle}
                            pageIcon={pageIcon}
                            showDefaultPrimaryText={true}
                            mediaUrl={null} // No actual media in SelectFormat
                            adCreativeFormat={format}
                            mockPreview={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
